import React, { useEffect, useState } from "react";
import { Sparkles, Route, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, Avatar, LevelBadge, ProgressBar, EmptyState } from "@/components/shared";
import { STATUS_STYLE, progressForSkills } from "@/lib/helpers";

function VideoBlock({ fileId, url, label }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let rev;
    if (fileId) {
      api.get(`/curriculum/skill-video/${fileId}`, { responseType: "blob" })
        .then((r) => { rev = URL.createObjectURL(r.data); setSrc(rev); }).catch(() => {});
    } else { setSrc(""); }
    return () => { if (rev) URL.revokeObjectURL(rev); };
  }, [fileId]);
  if (fileId && src) return <video data-testid="skill-video-file" src={src} controls className="mt-2 rounded-lg w-full max-w-sm" />;
  if (url) return <a data-testid="skill-video-link" href={url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-teal-dark underline text-sm">▶ {label} video</a>;
  return null;
}

export default function Progress() {
  const { user } = useAuth();
  const [dancers, setDancers] = useState([]);
  const [active, setActive] = useState(null);
  const [styles, setStyles] = useState([]);
  const [levels, setLevels] = useState([]);
  const [skills, setSkills] = useState([]);
  const [prog, setProg] = useState({ styles: [], skills: [] });
  const [openSk, setOpenSk] = useState({});

  useEffect(() => {
    api.get("/people/dancers").then((r) => { setDancers(r.data); if (r.data[0]) setActive(r.data[0].id); });
    api.get("/curriculum/styles").then((r) => setStyles(r.data));
    api.get("/curriculum/levels").then((r) => setLevels(r.data));
    api.get("/curriculum/skills").then((r) => setSkills(r.data));
  }, []);
  useEffect(() => { if (active) api.get(`/curriculum/progress/${active}`).then((r) => setProg(r.data)); }, [active]);

  const styleMap = Object.fromEntries(styles.map((s) => [s.id, s]));
  const levelMap = Object.fromEntries(levels.map((l) => [l.id, l]));
  const progMap = Object.fromEntries(prog.skills.map((p) => [p.skill_id, p.status]));
  const activeDancer = dancers.find((d) => d.id === active);

  return (
    <div className="stagger-in">
      <PageHeader title={user?.role === "parent" ? "My Dancers" : "My Progress"} subtitle="Levels, skills and the journey ahead." testid="progress-page" />

      {user?.role === "parent" && dancers.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {dancers.map((d) => (
            <button key={d.id} data-testid={`switch-dancer-${d.id}`} onClick={() => setActive(d.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold border ${active === d.id ? "bg-black text-white border-black" : "bg-white"}`}>
              <Avatar first={d.first_name} last={d.last_name} size={24} />{d.first_name}
            </button>
          ))}
        </div>
      )}

      {!activeDancer ? <EmptyState icon={Sparkles} title="No dancer data yet" /> :
        <>
          {prog.styles.length === 0 ? <EmptyState icon={Sparkles} title="No style progress yet" hint="Your teacher will start assessing soon!" /> :
            <div className="grid md:grid-cols-2 gap-5">
              {prog.styles.map((sp) => {
                const lvl = levelMap[sp.current_level_id];
                const styleSkills = skills.filter((sk) => sk.level_id === sp.current_level_id);
                const reqIds = styleSkills.filter((s) => s.required).map((s) => s.id);
                const pct = progressForSkills(prog.skills, reqIds);
                return (
                  <div key={sp.id} data-testid={`progress-style-${sp.style_id}`} className="bg-white rounded-2xl border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-head font-bold text-lg">{styleMap[sp.style_id]?.name}</h3>
                      <LevelBadge name={lvl?.name} color={lvl?.color} />
                    </div>
                    <ProgressBar value={pct} color={lvl?.color || "#2FB7B5"} />
                    <p className="text-sm text-muted-foreground mt-2 mb-4">{pct}% of required skills mastered</p>
                    <div className="space-y-1.5">
                      {styleSkills.map((sk) => {
                        const st = progMap[sk.id] || "Not Started";
                        const hasHome = sk.home_practice || sk.student_video || sk.home_video_file_id;
                        return (
                          <div key={sk.id}>
                            <button data-testid={`dancer-skill-${sk.id}`} onClick={() => hasHome && setOpenSk((o) => ({ ...o, [sk.id]: !o[sk.id] }))} className="w-full flex items-center justify-between text-sm py-1 text-left">
                              <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_STYLE[st].dot }} />{sk.name}{hasHome && <span className="text-[10px] text-teal-dark font-semibold">practice ▾</span>}</span>
                              <span className="text-xs font-semibold" style={{ color: STATUS_STYLE[st].dot }}>{st}</span>
                            </button>
                            {openSk[sk.id] && hasHome && (
                              <div className="ml-4 mb-2 p-3 rounded-lg bg-background" data-testid={`dancer-home-${sk.id}`}>
                                {sk.home_practice && <p className="text-sm"><b>Home practice:</b> {sk.home_practice}</p>}
                                <VideoBlock fileId={sk.home_video_file_id} url={sk.student_video} label="Home practice" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>}

          {/* Journey */}
          <h3 className="font-head font-bold text-lg mt-8 mb-4 flex items-center gap-2"><Route className="w-5 h-5 text-teal" />My Journey</h3>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
            {prog.styles.map((sp) => {
              const cur = levelMap[sp.current_level_id];
              const styleLevels = levels.filter((l) => l.style_id === sp.style_id).sort((a, b) => a.display_order - b.display_order);
              const curIdx = styleLevels.findIndex((l) => l.id === sp.current_level_id);
              return (
                <div key={sp.id}>
                  <p className="font-semibold mb-3">{styleMap[sp.style_id]?.name}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {styleLevels.map((l, i) => (
                      <React.Fragment key={l.id}>
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${i < curIdx ? "opacity-60" : ""} ${l.id === cur?.id ? "ring-2 ring-teal" : ""}`} style={{ background: `${l.color}1F` }}>
                          {i <= curIdx && <Trophy className="w-3.5 h-3.5" style={{ color: l.color }} />}
                          <span className="text-xs font-bold uppercase" style={{ color: l.color }}>{l.name}</span>
                        </div>
                        {i < styleLevels.length - 1 && <span className="text-gray-300">→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>}
    </div>
  );
}
